<!--
  CollectorAnswerCards — «العضوة ردّت على «{product} · كود {code}»: {instruction}»
  (collector-app P3-C, L-G). When a read finds a question she has NEWLY
  answered, ONE card lands on the day page next to the conflict cards, with
  «افتح {shop}» to go and buy it. No sound, no push, nothing that interrupts a
  man standing at a counter — he finds it the next time he looks at his day.

  Kept until dismissed («تمام», or opening the shop, which dismisses it), on
  the phone too: the cards are the same records as the conflict cards
  (`conflicts` in collector/actions.js and the phone's storage), told apart by
  `state: 'answered'`. The instruction is the server's sentence, printed as
  sent. Green when it is a yes (a thing to buy), amber when it says leave it.
-->
<template>
  <section v-if="cards.length" class="c-answers">
    <article
      v-for="card in cards"
      :key="card.id"
      class="c-answer-card"
      :class="card.response === 'yes' ? 'c-answer-card--go' : 'c-answer-card--stop'"
      :data-response="card.response"
    >
      <div class="c-answer-card__row">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          class="c-answer-card__icon"
        ><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>
        <div class="c-answer-card__text">
          {{ text(card) }}
        </div>
      </div>
      <div class="c-answer-card__acts">
        <button
          v-if="opensShop(card)"
          type="button"
          class="c-reset c-answer-card__btn c-answer-card__btn--primary"
          @click="openShop(card)"
        >
          {{ t('collector.conflicts.openShop', { shop: card.shopName }) }}
        </button>
        <button
          type="button"
          class="c-reset c-answer-card__btn"
          @click="actions.dismissConflict(card.id)"
        >
          {{ t('collector.conflicts.ok') }}
        </button>
      </div>
    </article>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCollectorActionsStore } from '@/collector/actions.js';

const emit = defineEmits(['open-shop']);

const { t } = useI18n();
const actions = useCollectorActionsStore();

const cards = computed(() => actions.conflicts.filter((card) => card.state === 'answered'));

/** «بلوزة حريمي قطن · كود 1023» */
function item(card) {
  if (!card.productName) return t('collector.conflicts.itemFallback');
  return card.memberCode
    ? t('collector.conflicts.item', { product: card.productName, code: card.memberCode })
    : card.productName;
}

function text(card) {
  return card.instruction
    ? t('collector.answerCard.text', { item: item(card), instruction: card.instruction })
    : t('collector.answerCard.textNoInstruction', { item: item(card) });
}

function opensShop(card) {
  return card.shopKey != null && Boolean(card.shopName);
}

function openShop(card) {
  actions.dismissConflict(card.id);
  emit('open-shop', card.shopKey);
}
</script>

<style scoped>
.c-answers {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 14px 16px 0;
}

.c-answer-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: var(--c-card);
  border-radius: var(--c-radius-row);
}

.c-answer-card--go { border: 2px solid var(--c-green-text); }
.c-answer-card--go .c-answer-card__icon { color: var(--c-green-text); }
.c-answer-card--stop { border: 2px solid var(--c-amber-border); }
.c-answer-card--stop .c-answer-card__icon { color: var(--c-amber-text); }

.c-answer-card__row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.c-answer-card__icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.c-answer-card__text {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.55;
}

.c-answer-card__acts {
  display: flex;
  gap: 8px;
}

.c-answer-card__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  height: 44px;
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
  color: var(--c-indigo);
  font-size: 15px;
  font-weight: 700;
}

.c-answer-card__btn--primary {
  flex-grow: 2;
  background: var(--c-indigo);
  color: #ffffff;
}
</style>
